import { useEffect, useRef, useState } from 'react'
import { Building2, Crosshair, Eye, FileText, Loader2, Plane, Search, X } from 'lucide-react'
import { apiClient } from '@/lib/api'
import type { AirLayer, AirSearchResult, AirStats, AtcFeed, AirHealth } from '@/types/airTraffic'
import AtcPlayer from './AtcPlayer'
import { AIR, CLASS_COLOR, FONT_MONO, classify, fmtAlt } from './theme'

const LAYER_CHIPS: Array<{ id: AirLayer; label: string; color: string; stat?: keyof AirStats; title: string }> = [
  { id: 'mil', label: 'Military', color: AIR.military, stat: 'mil', title: 'Military and government aircraft, worldwide' },
  { id: 'emg', label: 'Emergency', color: AIR.emergency, stat: 'emg', title: 'Squawking 7500 / 7600 / 7700, worldwide' },
  { id: 'priv', label: 'Privacy', color: AIR.privacy, stat: 'priv', title: 'LADD / PIA aircraft hidden by FR24 and FlightAware, worldwide' },
  { id: 'area', label: 'All traffic', color: AIR.civil, stat: 'in_view', title: 'Every aircraft in the current view (zoom in past continent level)' },
  { id: 'watched', label: 'Watched', color: AIR.watched, title: 'Aircraft matching your watch rules' },
]

interface Props {
  layers: Set<AirLayer>
  onToggleLayer: (l: AirLayer) => void
  stats: AirStats | null
  health: AirHealth | null
  connected: boolean
  lastFrameAt: number | null
  areaReason: 'zoom' | null
  watchedCount: number
  onPickAircraft: (hex: string, lon?: number, lat?: number) => void
  onPickAirport: (icao: string, lon?: number, lat?: number) => void
  onPickOperator: (name: string) => void
  onOpenArea: () => void
  onOpenRules: () => void
  atcFeed: AtcFeed | null
  onStopAtc: () => void
}

function useUtcClock() {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000)
    return () => window.clearInterval(id)
  }, [])
  return now.toISOString().slice(11, 19)
}

export default function TopBar(p: Props) {
  const clock = useUtcClock()
  const [q, setQ] = useState('')
  const [res, setRes] = useState<AirSearchResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setRes(null)
      return
    }
    setBusy(true)
    const t = window.setTimeout(async () => {
      try {
        setRes(await apiClient.airSearch(term))
      } catch {
        setRes({ aircraft: [], airports: [], operators: [] })
      } finally {
        setBusy(false)
      }
    }, 300)
    return () => window.clearTimeout(t)
  }, [q])

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const close = () => {
    setOpen(false)
    setQ('')
  }

  const stale = p.lastFrameAt ? Date.now() - p.lastFrameAt > 45000 : true
  const healthColor = !p.connected ? AIR.emergency : stale ? AIR.military : AIR.accent
  const healthText = !p.connected ? 'Reconnecting' : p.health?.mode === 'economy' ? 'Live · economy' : 'Live'
  const nothing = res && !res.aircraft.length && !res.airports.length && !res.operators.length

  return (
    <div
      className="flex h-11 shrink-0 items-center gap-3 border-b px-3"
      style={{ background: AIR.panel, borderColor: AIR.line }}
    >
      <div ref={boxRef} className="relative w-[320px] shrink-0">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2" style={{ color: AIR.muted }} />
        <input
          id="air-search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === 'Escape' && close()}
          placeholder="Callsign, hex, registration, operator or airport"
          className="h-7 w-full rounded-sm pl-7 pr-7 text-[12px] outline-none placeholder:opacity-60 focus:ring-1"
          style={{ background: AIR.bg, color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.lineStrong}`, ['--tw-ring-color' as any]: AIR.accent }}
          autoComplete="off"
        />
        {busy ? (
          <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin" style={{ color: AIR.muted }} />
        ) : q ? (
          <button type="button" onClick={close} className="absolute right-1.5 top-1/2 -translate-y-1/2" aria-label="Clear search">
            <X className="h-3.5 w-3.5" style={{ color: AIR.muted }} />
          </button>
        ) : null}

        {open && res && (
          <div
            className="absolute left-0 top-9 z-30 max-h-[420px] w-[420px] overflow-y-auto rounded-sm border py-1 shadow-2xl"
            style={{ background: AIR.panel, borderColor: AIR.lineStrong }}
          >
            {nothing && (
              <div className="px-3 py-2 text-[12px]" style={{ color: AIR.muted }}>
                Nothing airborne or on file matches “{q.trim()}”.
              </div>
            )}
            {res.aircraft.length > 0 && <Group label="Aircraft" />}
            {res.aircraft.map((a) => {
              const cls = classify(a, new Set())
              return (
                <Row
                  key={a.hex}
                  icon={<Plane className="h-3.5 w-3.5" style={{ color: CLASS_COLOR[cls], transform: `rotate(${(a.trk ?? 45) - 45}deg)` }} />}
                  title={a.cs || a.reg || a.hex.toUpperCase()}
                  sub={[a.type, a.reg, a.op, fmtAlt(a)].filter(Boolean).join(' · ')}
                  onClick={() => {
                    p.onPickAircraft(a.hex, a.lon, a.lat)
                    close()
                  }}
                />
              )
            })}
            {res.airports.length > 0 && <Group label="Airports" />}
            {res.airports.map((a) => (
              <Row
                key={a.icao || a.iata || a.name}
                icon={<Building2 className="h-3.5 w-3.5" style={{ color: a.atc ? AIR.accent : AIR.muted }} />}
                title={`${a.icao || ''}${a.iata ? ` / ${a.iata}` : ''}`}
                sub={`${a.name}${a.city ? `, ${a.city}` : ''}${a.atc ? ' · ATC audio' : ''}`}
                onClick={() => {
                  if (a.icao) p.onPickAirport(a.icao, a.lon, a.lat)
                  close()
                }}
              />
            ))}
            {res.operators.length > 0 && <Group label="Operators airborne now" />}
            {res.operators.map((o) => (
              <Row
                key={o}
                icon={<Eye className="h-3.5 w-3.5" style={{ color: AIR.muted }} />}
                title={o}
                sub="Operator activity"
                onClick={() => {
                  p.onPickOperator(o)
                  close()
                }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex min-w-0 items-center gap-1 overflow-x-auto">
        {LAYER_CHIPS.map((c) => {
          const on = p.layers.has(c.id)
          const count = c.id === 'watched' ? p.watchedCount : c.stat ? p.stats?.[c.stat] : undefined
          const disabled = c.id === 'area' && p.areaReason === 'zoom' && on
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => p.onToggleLayer(c.id)}
              title={disabled ? 'Zoom in to see all traffic. Wide views show the worldwide layers only.' : c.title}
              aria-pressed={on}
              className="flex h-7 shrink-0 items-center gap-1.5 rounded-sm px-2 text-[11px] transition-colors"
              style={{
                background: on ? `${c.color}14` : 'transparent',
                boxShadow: `inset 0 0 0 1px ${on ? `${c.color}55` : AIR.line}`,
                color: on ? AIR.text : AIR.muted,
              }}
            >
              <span className="h-2 w-2 rounded-full" style={{ background: on ? c.color : 'transparent', boxShadow: `0 0 0 1px ${c.color}` }} />
              {c.label}
              <span style={{ fontFamily: FONT_MONO, color: on ? c.color : AIR.dim }}>
                {disabled ? 'zoom in' : count ?? '—'}
              </span>
            </button>
          )
        })}
      </div>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        <AtcPlayer feed={p.atcFeed} onStop={p.onStopAtc} />
        <IconButton title="Analyse the current view" onClick={p.onOpenArea}>
          <Crosshair className="h-3.5 w-3.5" /> This view
        </IconButton>
        <IconButton title="Watch rules and alert settings" onClick={p.onOpenRules}>
          <FileText className="h-3.5 w-3.5" /> Watch rules
        </IconButton>
        <div className="flex items-center gap-1.5 pl-1" title={healthText}>
          <span className={`h-2 w-2 rounded-full ${p.connected && !stale ? 'animate-pulse' : ''}`} style={{ background: healthColor }} />
          <span className="text-[11px]" style={{ color: AIR.muted }}>
            {healthText}
          </span>
        </div>
        <span className="text-[12px] tabular-nums" style={{ fontFamily: FONT_MONO, color: AIR.bright }}>
          {clock}
          <span className="ml-1 text-[10px]" style={{ color: AIR.muted }}>
            UTC
          </span>
        </span>
      </div>
    </div>
  )
}

function Group({ label }: { label: string }) {
  return (
    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: AIR.muted }}>
      {label}
    </div>
  )
}

function Row({ icon, title, sub, onClick }: { icon: React.ReactNode; title: string; sub: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-white/[0.04]">
      {icon}
      <span className="min-w-0">
        <span className="block truncate text-[12px]" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
          {title}
        </span>
        <span className="block truncate text-[11px]" style={{ color: AIR.muted }}>
          {sub}
        </span>
      </span>
    </button>
  )
}

function IconButton({ children, onClick, title }: { children: React.ReactNode; onClick: () => void; title: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="flex h-7 items-center gap-1.5 rounded-sm px-2 text-[11px] hover:bg-white/[0.04]"
      style={{ color: AIR.text, boxShadow: `inset 0 0 0 1px ${AIR.line}` }}
    >
      {children}
    </button>
  )
}
